<template>
  <Modal
    v-model="visible"
    title="永诀宝典"
    width="920"
    :footer-hide="true"
    class-name="yongjue-codex-shell"
    @on-visible-change="handleVisibleChange"
  >
    <div class="codex-panel">
      <header class="codex-heading">
        <div>
          <span class="codex-kicker">修行指引</span>
          <h2>永诀宝典</h2>
          <p>循章完成修行目标，逐项领取任务奖励；完成全章后可领取章节奖励。</p>
        </div>
        <div class="codex-summary">
          <span><small>总目标</small><b>{{ overview.totalTaskCount || 0 }}</b></span>
          <span><small>已领取</small><b>{{ overview.claimedTaskCount || 0 }}</b></span>
          <span class="ready"><small>待领取</small><b>{{ overview.claimableTaskCount || 0 }}</b></span>
          <Button icon="md-refresh" size="small" :loading="loading" @click="loadOverview">刷新</Button>
        </div>
      </header>

      <Spin fix v-if="loading" />
      <div v-if="!loading && !chapters.length" class="codex-empty">
        <Icon type="ios-book-outline" size="46" />
        <p>宝典章节尚未开放</p>
      </div>

      <section v-for="chapter in chapters" :key="chapter.chapterId" class="codex-chapter">
        <div class="chapter-heading">
          <div>
            <span>第 {{ chapter.chapterNo }} 章</span>
            <h3>{{ chapter.chapterName }}</h3>
            <p>{{ chapter.chapterDesc }}</p>
          </div>
          <div class="chapter-progress">
            <strong>{{ chapter.claimedTaskCount || 0 }} / {{ chapter.taskCount || 0 }}</strong>
            <Progress :percent="chapterPercent(chapter)" :stroke-width="6" hide-info />
          </div>
        </div>

        <div class="codex-task-list">
          <article
            v-for="task in chapter.tasks || []"
            :key="task.taskId"
            class="codex-task"
            :class="'status-' + taskStatus(task)"
          >
            <div class="task-mark">
              <Icon :type="statusIcon(task)" size="20" />
            </div>
            <div class="task-body">
              <div class="task-title-row">
                <div>
                  <div class="task-name-line">
                    <h4>{{ task.taskName }}</h4>
                    <Tag v-if="levelRequirementText(task)" color="orange" class="task-level-tag">
                      {{ levelRequirementText(task) }}接取
                    </Tag>
                  </div>
                  <p v-html="task.taskDesci"></p>
                </div>
                <Tag :color="statusColor(task)">{{ statusText(task) }}</Tag>
              </div>

              <div v-if="task.needRule && task.needRule.length" class="task-requirements">
                <div v-for="(rule, index) in task.needRule" :key="rule.dtlId || index" class="requirement-row">
                  <span>{{ rule.dtlNeedName || requirementName(rule.dtlType) }}</span>
                  <div>
                    <Progress :percent="rulePercent(rule)" :stroke-width="5" hide-info />
                    <b>{{ Number(rule.dtlSubNum || 0) }} / {{ Number(rule.dtlNeedNum || 0) }}</b>
                  </div>
                </div>
              </div>

              <div class="task-footer">
                <div class="task-rewards">
                  <span class="reward-label">任务奖励</span>
                  <span v-if="Number(task.rewardExp || 0) > 0" class="reward-chip">修为 +{{ task.rewardExp }}</span>
                  <template v-if="task.rewardRule && task.rewardRule.length">
                    <label
                      v-for="reward in task.rewardRule"
                      :key="reward.itemId"
                      class="reward-chip"
                      :class="{ selectable: task.appoint === 1, selected: selectedRewards[task.taskId] === reward.itemId }"
                    >
                      <input
                        v-if="task.appoint === 1"
                        type="radio"
                        :name="'codex-reward-' + task.taskId"
                        :value="reward.itemId"
                        :checked="selectedRewards[task.taskId] === reward.itemId"
                        @change="$set(selectedRewards, task.taskId, reward.itemId)"
                      >
                      {{ reward.itemName }} x{{ reward.dropNum }}
                    </label>
                  </template>
                  <span v-if="!Number(task.rewardExp || 0) && (!task.rewardRule || !task.rewardRule.length)" class="reward-chip muted">按任务配置发放</span>
                </div>
                <div class="task-actions">
                  <Button v-if="taskStatus(task) === 0" size="small" type="primary" @click="acceptTask(task)">接取</Button>
                  <Button v-else-if="taskStatus(task) === 1" size="small" icon="md-refresh" @click="loadOverview">刷新进度</Button>
                  <Button v-else-if="taskStatus(task) === 2" size="small" type="success" icon="ios-gift" @click="claimTask(task)">领取</Button>
                  <Button v-else size="small" disabled icon="md-checkmark">已领取</Button>
                </div>
              </div>
            </div>
          </article>
        </div>

        <footer class="chapter-reward">
          <div>
            <span>章节奖励</span>
            <strong v-if="chapter.rewardCoin">灵石 +{{ chapter.rewardCoin }}</strong>
            <strong v-if="chapter.rewardMoney">金币 +{{ chapter.rewardMoney }}</strong>
            <strong v-if="chapter.rewardExp">修为 +{{ chapter.rewardExp }}</strong>
          </div>
          <Button
            type="warning"
            :disabled="!chapter.claimable"
            :loading="claimingChapterId === chapter.chapterId"
            @click="claimChapter(chapter)"
          >
            {{ Number(chapter.rewardStatus) === 2 ? '已领取' : (chapter.claimable ? '领取章节奖励' : '完成全部目标后领取') }}
          </Button>
        </footer>
      </section>
    </div>
  </Modal>
</template>

<script>
export default {
  name: 'YongjueCodexModal',
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      visible: this.value,
      loading: false,
      overview: {},
      selectedRewards: {},
      claimingChapterId: null
    }
  },
  computed: {
    chapters () {
      return Array.isArray(this.overview.chapters) ? this.overview.chapters : []
    }
  },
  watch: {
    value (next) {
      this.visible = next
      if (next) this.loadOverview()
    },
    visible (next) {
      this.$emit('input', next)
    }
  },
  methods: {
    handleVisibleChange (visible) {
      if (visible) this.loadOverview()
    },
    getCookie (name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      return parts.length === 2 ? parts.pop().split(';').shift() : ''
    },
    async loadOverview () {
      const charaId = this.getCookie('charaId')
      if (!charaId || this.loading) return
      this.loading = true
      try {
        const res = await this.$http.post(`/gameCodex/overview?charaId=${encodeURIComponent(charaId)}`)
        if (res.data.status === 200) {
          this.overview = res.data.data || {}
          this.$emit('overview-updated', this.overview)
        } else this.$Message.warning(res.data.msg || '宝典加载失败')
      } catch (error) {
        this.$Message.warning(this.errorMessage(error, '宝典加载失败'))
      } finally {
        this.loading = false
      }
    },
    async acceptTask (task) {
      await this.runTaskAction(`/gameCodex/acceptTask?charaId=${encodeURIComponent(this.getCookie('charaId'))}&taskId=${task.taskId}`, '接取成功')
    },
    async claimTask (task) {
      const rewardId = Number(this.selectedRewards[task.taskId] || 0)
      if (Number(task.appoint) === 1 && !rewardId) {
        this.$Message.warning('请先选择一项任务奖励')
        return
      }
      await this.runTaskAction(`/gameCodex/claimTask?charaId=${encodeURIComponent(this.getCookie('charaId'))}&taskId=${task.taskId}&rewardId=${rewardId}`, '领取成功')
    },
    async runTaskAction (url, successText) {
      try {
        const res = await this.$http.post(url)
        if (res.data.status === 200) {
          this.$Message.success(successText)
          await this.loadOverview()
        } else this.$Message.warning(res.data.msg || '操作失败')
      } catch (error) {
        this.$Message.warning(this.errorMessage(error, '操作失败'))
      }
    },
    async claimChapter (chapter) {
      if (!chapter.claimable) return
      this.claimingChapterId = chapter.chapterId
      try {
        const res = await this.$http.post(`/gameCodex/claimChapter?charaId=${encodeURIComponent(this.getCookie('charaId'))}&chapterId=${chapter.chapterId}`)
        if (res.data.status === 200) {
          this.$Message.success('章节奖励领取成功')
          await this.loadOverview()
        } else this.$Message.warning(res.data.msg || '章节奖励领取失败')
      } catch (error) {
        this.$Message.warning(this.errorMessage(error, '章节奖励领取失败'))
      } finally {
        this.claimingChapterId = null
      }
    },
    taskStatus (task) {
      const status = Number(task.status)
      return [0, 1, 2, 3].indexOf(status) >= 0 ? status : 0
    },
    statusText (task) {
      return ['未接取', '进行中', '可领取', '已领取'][this.taskStatus(task)]
    },
    statusColor (task) {
      return ['default', 'blue', 'green', 'default'][this.taskStatus(task)]
    },
    statusIcon (task) {
      return ['ios-radio-button-off', 'ios-time-outline', 'ios-gift', 'md-checkmark-circle'][this.taskStatus(task)]
    },
    requirementName (type) {
      const names = { monster: '击败怪物', skill: '技能目标', equip: '装备目标', level: '等级目标', map: '地图目标' }
      return names[type] || '修行目标'
    },
    levelRequirementText (task) {
      const minLevel = Number(task && task.minLv)
      const maxLevel = Number(task && task.maxLv)
      if (minLevel > 0 && maxLevel > 0 && minLevel === maxLevel) return `Lv.${minLevel}`
      if (minLevel > 0 && maxLevel > 0) return `Lv.${minLevel}-${maxLevel}`
      if (minLevel > 0) return `Lv.${minLevel}+`
      if (maxLevel > 0) return `Lv.1-${maxLevel}`
      return ''
    },
    rulePercent (rule) {
      const target = Number(rule.dtlNeedNum || 0)
      return target <= 0 ? 0 : Math.min(100, Math.round(Number(rule.dtlSubNum || 0) * 100 / target))
    },
    chapterPercent (chapter) {
      const total = Number(chapter.taskCount || 0)
      return total <= 0 ? 0 : Math.round(Number(chapter.claimedTaskCount || 0) * 100 / total)
    },
    errorMessage (error, fallback) {
      return (error && error.response && error.response.data && error.response.data.msg) || (error && error.data && error.data.msg) || fallback
    }
  }
}
</script>

<style scoped>
.codex-panel { position: relative; min-height: 260px; color: #24342c; }
.codex-heading { display: flex; justify-content: space-between; gap: 24px; padding: 6px 2px 18px; border-bottom: 1px solid #d7dfda; }
.codex-kicker { color: #8a6b22; font-size: 12px; font-weight: 700; }
.codex-heading h2 { margin: 3px 0 4px; font-size: 24px; letter-spacing: 0; }
.codex-heading p, .chapter-heading p, .task-title-row p { margin: 0; color: #718077; }
.codex-summary { display: flex; align-items: center; gap: 9px; }
.codex-summary > span { min-width: 58px; padding: 7px 9px; text-align: center; background: #f3f6f4; border: 1px solid #d6dfda; }
.codex-summary small, .codex-summary b { display: block; }
.codex-summary b { font-size: 17px; }
.codex-summary .ready { color: #287a4f; background: #eef8f1; border-color: #b9dac5; }
.codex-chapter { margin-top: 18px; border: 1px solid #cfd9d3; background: #fbfcfb; }
.chapter-heading { display: flex; justify-content: space-between; gap: 18px; padding: 15px 17px; background: #eef3ef; border-bottom: 1px solid #d5ddd8; }
.chapter-heading span { color: #7a622c; font-size: 12px; font-weight: 700; }
.chapter-heading h3 { margin: 2px 0 3px; font-size: 18px; letter-spacing: 0; }
.chapter-progress { width: 130px; text-align: right; }
.chapter-progress strong { display: block; margin-bottom: 5px; }
.codex-task-list { display: grid; gap: 0; }
.codex-task { display: grid; grid-template-columns: 38px 1fr; padding: 14px 16px; border-bottom: 1px solid #e2e7e4; }
.codex-task:last-child { border-bottom: 0; }
.task-mark { width: 28px; height: 28px; display: grid; place-items: center; color: #7c8982; background: #edf1ef; border: 1px solid #d2dad6; }
.status-2 .task-mark { color: #25754b; background: #e8f6ed; border-color: #a9d2b7; }
.status-3 { opacity: .72; }
.task-title-row { display: flex; justify-content: space-between; gap: 15px; }
.task-name-line { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; margin-bottom: 3px; }
.task-title-row h4 { margin: 0 0 3px; font-size: 15px; letter-spacing: 0; }
.task-name-line h4 { margin-bottom: 0; }
.task-level-tag { flex: 0 0 auto; margin: 0; }
.task-title-row p { line-height: 1.5; }
.task-requirements { display: grid; gap: 6px; margin-top: 10px; }
.requirement-row { display: grid; grid-template-columns: minmax(130px, 1fr) minmax(180px, 260px); align-items: center; gap: 12px; font-size: 12px; }
.requirement-row > div { display: grid; grid-template-columns: 1fr 64px; align-items: center; gap: 8px; }
.requirement-row b { text-align: right; }
.task-footer { display: flex; justify-content: space-between; align-items: flex-end; gap: 14px; margin-top: 11px; }
.task-rewards { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.reward-label { color: #78857e; font-size: 12px; }
.reward-chip { padding: 3px 7px; color: #6b541f; background: #faf4df; border: 1px solid #dfcf95; font-size: 12px; }
.reward-chip.selectable { cursor: pointer; }
.reward-chip.selected { color: #225b42; background: #e7f3eb; border-color: #83b699; }
.reward-chip input { margin: 0 3px 0 0; vertical-align: -1px; }
.reward-chip.muted { color: #7d8782; background: #f2f4f3; border-color: #d7ddda; }
.task-actions { flex: 0 0 auto; }
.chapter-reward { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 13px 17px; background: #f7f1df; border-top: 1px solid #ddcf9e; }
.chapter-reward div { display: flex; flex-wrap: wrap; gap: 12px; }
.chapter-reward span { color: #806527; font-weight: 700; }
.chapter-reward strong { font-size: 13px; }
.codex-empty { padding: 60px 20px; text-align: center; color: #8a9690; }
@media (max-width: 768px) {
  .codex-heading, .chapter-heading, .task-footer { align-items: stretch; flex-direction: column; }
  .codex-summary { display: grid; grid-template-columns: repeat(3, 1fr); }
  .codex-summary .ivu-btn { grid-column: 1 / -1; }
  .chapter-progress { width: 100%; text-align: left; }
  .codex-task { grid-template-columns: 30px 1fr; padding: 12px 10px; }
  .requirement-row { grid-template-columns: 1fr; gap: 4px; }
  .chapter-reward { align-items: stretch; flex-direction: column; }
}
</style>

<style>
.yongjue-codex-shell .ivu-modal { max-width: calc(100vw - 20px); }
.yongjue-codex-shell .ivu-modal-body { max-height: 76vh; overflow-y: auto; }
</style>



// WEBPACK FOOTER //
// src/components/YongjueCodexModal.vue